import { ChevronLeftIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import api from "@src/api/api";
import Input from "@src/components/elements/input/Input";
import ShowMoreButton from "@src/components/elements/input/ShowMoreButton";
import TabBodies from "@src/components/elements/shared/tab/TabBodies";
import TabBody from "@src/components/elements/shared/tab/TabBody";
import TabController from "@src/components/elements/shared/tab/TabController";
import TabHeader from "@src/components/elements/shared/tab/TabHeader";
import TabHeaders from "@src/components/elements/shared/tab/TabHeaders";
import Watch from "@src/pages/watch/Watch";
import { useAppSelector } from "@src/state/hooks";
import WatchType from "@src/utils/types/watch/WatchType";
import { useInfiniteQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import tw from "twin.macro";

type Props = {
  tab?: string;
};

type Response = {
  data: Array<WatchType>;
  current_page: number;
  total_pages: number;
};

const WatchContainer = ({ tab = "toYou" }: Props) => {
  const [searchParams] = useSearchParams();
  const featured = searchParams.get("featured");
  const [muted, setMuted] = useState(true);
  const [activePost, setActivePost] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState("");
  const { user } = useAppSelector(state => state.user);
  const { t } = useTranslation();

  const posts = useInfiniteQuery<Response>(
    ["watch", "posts", tab, searchQuery, searchParams.get("featured")],
    async ({ pageParam = 0 }) => {
      let queryString = "";
      if (searchQuery) queryString = `&q=${searchQuery}`;
      if (user && tab === "myWatch") queryString = `&user_id=${user.id}`;
      if (tab === "following") queryString = "&following=true";
      const { data } = await api.get(`/api/v1/watch?page=${pageParam}&${queryString}`);
      return data;
    },
    {
      getNextPageParam: lastPage => {
        const nextPage = lastPage.current_page + 1;
        return nextPage <= lastPage.total_pages ? nextPage : false;
      },
    },
  );

  const processPosts = (posts: Response[]) => {
    if (!posts) return [];
    let allPosts = posts.flatMap(item => item.data);
    if (featured) {
      const featuredIndex = allPosts.findIndex(post => post.media_id === featured);
      if (featuredIndex > -1) {
        const [featuredPost] = allPosts.splice(featuredIndex, 1);
        allPosts = [featuredPost, ...allPosts];
      }
    }
    return allPosts;
  };

  function scrollPosts({ e, delta = 0 }: { e: React.UIEvent<HTMLDivElement>; delta?: number }) {
    if (!e.currentTarget) return;
    if (e.currentTarget.scrollTop === 0 && activePost !== 0) return setActivePost(0);

    if (delta === 0)
      setActivePost(Math.round(e.currentTarget.scrollTop / e.currentTarget.clientHeight));
    if (delta < 0)
      setActivePost(Math.floor(e.currentTarget.scrollTop / e.currentTarget.clientHeight));
    if (delta > 0)
      setActivePost(Math.ceil(e.currentTarget.scrollTop / e.currentTarget.clientHeight));
  }

  const count = (posts.isSuccess && posts.data.pages.length * 30) || 0;

  const container = (
    <div
      className="mx-auto grid h-[calc(100dvh-40px)] w-screen snap-y snap-mandatory grid-cols-1 overflow-y-scroll scrollbar-none md:mt-20 md:h-[calc(100vh-200px)] md:w-[calc((100dvh-300px)/16*9)] lg:mt-0 xl:h-[calc(100vh-180px)]"
      onScroll={e => {
        scrollPosts({ e: e });
      }}
      onWheel={e => {
        scrollPosts({ e: e, delta: e.deltaY });
      }}
    >
      {posts.isSuccess &&
        processPosts(posts.data?.pages).map((watch, i) => {
          return (
            <Watch
              queryKey={["watch", "posts", tab, searchQuery, searchParams.get("featured")]}
              key={`${watch.post_id}-${watch.media_id}`}
              data={watch}
              active={i === activePost}
              preload={activePost + 3 > i && activePost - 3 < i}
              autoplay={true}
              muted={muted}
              setMuted={(state: boolean) => setMuted(state)}
            />
          );
        })}
      {(posts.hasNextPage || posts.isFetchingNextPage) && (
        <ShowMoreButton onClick={() => posts.fetchNextPage()} />
      )}
    </div>
  );

  if (activePost > count - 5 && posts.hasNextPage) posts.fetchNextPage();

  return (
    <div>
      <TabController defaultTab={tab}>
        <TabHeaders
          headerCSS={tw`max-lg:absolute left-0 max-lg:z-[10] max-lg:bg-opacity-40 max-lg:brightness-125 max-lg:px-4 gap-3`}
          tabCSS={tw`max-lg:invert max-lg:grayscale`}
        >
          <TabHeader id="back" url="/news/" hideDesktop>
            <ChevronLeftIcon className="size-5 text-gray-600" />
          </TabHeader>
          <TabHeader id="myWatch" url="/watch/mein_watch">
            {t("main.watchTabs.myWatch")}
          </TabHeader>
          <TabHeader id="toYou" url="/watch/für_dich">
            {t("main.watchTabs.toYou")}
          </TabHeader>
          <TabHeader id="create" url="/watch/erstellen" hideMobile>
            {t("main.watchTabs.create.title")}
          </TabHeader>
          <TabHeader id="following" url="/watch/following">
            {t("main.watchTabs.following")}
          </TabHeader>
          <TabHeader id="trending" url="/watch/trending">
            {t("main.watchTabs.trending")}
          </TabHeader>
          <TabHeader id="search" url="/watch/search" hideDesktop>
            <MagnifyingGlassIcon className="flex size-5 items-center text-gray-600" />
          </TabHeader>
        </TabHeaders>
        <div className="pb-3 max-lg:hidden">
          <Input
            name="search-contacts"
            placeholder={t("components.shared.search", {
              tab: t("main.watchTabs." + tab),
            })}
            value={searchQuery}
            onChange={e => setSearchQuery(e)}
            clearable
          />
        </div>
        <TabBodies>
          <TabBody id="myWatch">{container}</TabBody>
          <TabBody id="toYou">{container}</TabBody>
          <TabBody id="following">{container}</TabBody>
          <TabBody id="trending">{container}</TabBody>
        </TabBodies>
      </TabController>
    </div>
  );
};

export default WatchContainer;
